import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { MobileDock } from "@/components/layout/MobileDock";
import { OnboardingWelcome } from "@/components/OnboardingWelcome";
import { useAccount } from "@/context/AccountContext";
import { api } from "@/lib/api";

export function AppShell() {
  const { currentKey } = useAccount();
  const [search, setSearch] = useState("");
  const [approvals, setApprovals] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const isChatRoute = location.pathname === "/chat";
  const [viewport, setViewport] = useState(null);

  useEffect(() => {
    if (!isChatRoute || !window.visualViewport) return undefined;
    const visible = window.visualViewport;
    let frame;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const editing = document.activeElement?.matches("input, textarea, [contenteditable='true']");
        // iOS changes the visual viewport independently of the layout viewport.
        const next = {
          height: visible.height,
          top: visible.offsetTop,
          keyboard: Boolean(editing && window.innerHeight - visible.height > 150),
        };
        setViewport((previous) => previous?.height === next.height && previous?.top === next.top && previous?.keyboard === next.keyboard ? previous : next);
      });
    };
    update();
    visible.addEventListener("resize", update);
    visible.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("pageshow", update);
    document.addEventListener("visibilitychange", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    return () => {
      cancelAnimationFrame(frame);
      visible.removeEventListener("resize", update);
      visible.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("pageshow", update);
      document.removeEventListener("visibilitychange", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
      setViewport(null);
    };
  }, [isChatRoute]);

  const loadApprovals = () => {
    if (!currentKey) return Promise.resolve();
    return api
      .get("/content", { params: { account: currentKey, status: "review" } })
      .then(({ data }) => setApprovals(data))
      .catch(() => setApprovals([]));
  };

  useEffect(() => {
    loadApprovals();
    const iv = setInterval(loadApprovals, 15000);
    const onFocus = () => loadApprovals();
    const onApprovalChange = () => loadApprovals();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("approvals:changed", onApprovalChange);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("approvals:changed", onApprovalChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  useEffect(() => {
    document.documentElement.classList.toggle("chat-route-lock", isChatRoute);
    document.body.classList.toggle("chat-route-lock", isChatRoute);
    if (isChatRoute) window.scrollTo({ top: 0, behavior: "auto" });
    return () => {
      document.documentElement.classList.remove("chat-route-lock");
      document.body.classList.remove("chat-route-lock");
    };
  }, [isChatRoute]);

  return (
    <div
      className={`liquid-app app-shell flex w-full max-w-full overflow-x-clip ${isChatRoute ? "app-shell-chat h-[100dvh] min-h-0 overflow-y-hidden" : "min-h-screen"}`}
      data-keyboard-open={isChatRoute && viewport?.keyboard ? "true" : undefined}
      // Ohne Tastatur darf kein einmal gemessener Pixelwert die dynamische
      // Safari-Höhe einfrieren (z. B. beim Zurückkehren aus WhatsApp).
      style={isChatRoute && viewport?.keyboard ? { "--app-viewport-height": `${viewport.height}px`, "--app-viewport-top": `${viewport.top}px` } : undefined}
    >
      <OnboardingWelcome />
      <Sidebar approvalsCount={approvals.length} mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
      <div className={`relative z-10 flex-1 min-w-0 flex flex-col lg:pr-3 ${isChatRoute ? "min-h-0 overflow-hidden" : ""}`}>
        <Topbar search={search} onSearch={setSearch} approvals={approvals} onMenuClick={() => setNavOpen(true)} />
        <main className={`app-main flex-1 min-w-0 max-w-full overflow-x-clip ${isChatRoute ? "min-h-0 overflow-y-hidden" : ""}`}>
          {currentKey ? (
            <Outlet context={{ search, accountKey: currentKey, refreshApprovals: loadApprovals }} />
          ) : (
            <div className="p-10 text-neutral-400">Lade Account…</div>
          )}
        </main>
        <MobileDock />
      </div>
    </div>
  );
}
